import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SportsCacheTemplateDisciplinaLiveDto, sportsCli } from 'common/types/swagger';
import { useAppDispatch } from 'lib/centralStore';
import { useRouter } from 'next/router';
import { isServerSide } from 'utility/constant';
import { getSlugFromPath, isCrawler, isNotEmpty } from 'utility/functions';
import { feedTemplateTopLive } from 'lib/ssr/sport/templates/top-live/actions';
import { SportsTemplateTopLive } from 'lib/ssr/sport/templates/top-live/types';

const indexing = isCrawler();

export const useTemplateTopLiveSnapshot = (data?: SportsTemplateTopLive): Promise<void> => {
  const dispatch = useAppDispatch();
  const { asPath, isReady, isFallback, events } = useRouter();

  const [isLoaded, setLoaded] = useState(false);
  const [isCompleted, setCompleted] = useState(false);

  const slug = useMemo(() => {
    if (isNotEmpty(data?.slug)) {
      return getSlugFromPath(asPath);
    }
    return data?.slug;
  }, [data, asPath]);

  const lastSlug = useRef('');
  const template = useRef<SportsCacheTemplateDisciplinaLiveDto>({
    ...data,
    slug,
  } as SportsCacheTemplateDisciplinaLiveDto);

  const doFetch = useCallback(async () => {
    setCompleted(false);
    const props = { slug: lastSlug.current };
    if (isServerSide) {
      Reflect.set(props, 'light', true);
    }
    sportsCli.live
      .templateTopList(props)
      .then((result) => {
        if (result.ok) {
          const templateTop = result.data ?? {};
          const payload = { ...templateTop, slug: lastSlug.current } as SportsCacheTemplateDisciplinaLiveDto;
          template.current = payload;
        }
      })
      .finally(() => setCompleted(true));
  }, []);

  useEffect(() => {
    if (isServerSide || isFallback || indexing) {
      return;
    }

    const nextSlug = `${slug}`?.toLowerCase();
    if (isNotEmpty(nextSlug) && lastSlug.current !== nextSlug) {
      lastSlug.current = nextSlug;
      doFetch();
    }
  }, [slug, isLoaded, isFallback, isCompleted, doFetch]);

  useEffect(() => {
    if ((isReady || isLoaded) && isCompleted) {
      // once HYDRATION and FETCH both completed -> FEED with fresh data
      dispatch(feedTemplateTopLive(template.current));
    }
  }, [isReady, isLoaded, isCompleted]);

  useEffect(() => {
    const handleChangeStart = () => setLoaded(false);
    const handleChangeComplete = () => setLoaded(true);

    events.on('routeChangeStart', handleChangeStart);
    events.on('routeChangeComplete', handleChangeComplete);

    return () => {
      events.off('routeChangeStart', handleChangeStart);
      events.off('routeChangeComplete', handleChangeComplete);
    };
  }, [events]);

  return Promise.resolve();
};